import { useCallback, useMemo, useState } from 'react';
import React from 'react';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import styles from './../styles.module.scss';
import { NifiEdgeData } from './types';
import { DataSourceHandler } from '@api/data-source-handler';
import FileSaver from 'file-saver';
import { AxiosResponse } from 'axios';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { TooltipTop } from '@components/tooltips';
import {
  GoToFullScreenIcon
} from "@pages/workflow-analytics-page/assets/icons";
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { Modal } from 'antd';
import Spinner from '@components/spinners/spinner';
import { RestartSVGIcon } from '@pages/cluster_redisign/icons';
import { ShowWhenTrue } from '@helpers/showwhentrue';

type NifiTilesProps = {
  nifiData: NifiEdgeData[],
  setNifiEdgeModal: (value: boolean) => void,
  setNifiEdgeDataModal: (data: NifiEdgeData) => void
  onEdgeNodeClick: (clickedNodeData: NifiEdgeData, time: string) => void;
  getNifiEdgeData: () => void;
  selectedNodeType: string;
  showPatches: (data: NifiEdgeData, type: string) => void;
  showInstallationLogs: (data: NifiEdgeData) => void;
  poolData: NifiEdgeData[];
}

const NifiTiles: React.FC<NifiTilesProps> = ({
  nifiData = [],
  setNifiEdgeModal,
  setNifiEdgeDataModal,
  onEdgeNodeClick,
  getNifiEdgeData,
  selectedNodeType,
  showPatches,
  showInstallationLogs,
  poolData
}) => {
  const [scaledNodesData, setScaledNodesData] = useState<any[]>([]);
  const history = useHistory();
  const [parentDataView, setParentDataView] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);

  const onDownloadCertificate = useCallback((id: number, _data: any) => {
    DataSourceHandler.DownloadEdgeCertificate(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, _data)
      .then((response: AxiosResponse<any>) => {
        // Extract filename from url
        const link = response.config.url;
        if (link) {
          const extractedWord = _data.nodeName ? _data.nodeName : _data.nodeScaleName
          let filename = `${extractedWord ? extractedWord : 'config'}.tar.gz`;
          if (extractedWord) {
            const filenameMatch = extractedWord.match(/filename="(.+)"/);
            if (filenameMatch && filenameMatch.length === 2) {
              filename = filenameMatch[1];
            }
          }
          FileSaver.saveAs(new Blob([response.data]), filename);
        } else {
          throw new Error("Failed to extract filename from the download link. Please try again later.");
        }
      })
      .catch(() => {
        errorAlert("Something went wrong, please try again after sometime");
      });
  }, []);




  const deleteScaleNode = useCallback((parentNode: string, nodeScaleName: string) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this scaled node?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        DataSourceHandler.DeleteScaleNodes(
          `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
          parentNode,
          nodeScaleName,
          (response) => {
            if (response) {
              successAlert(response);
              childNode(parentNode);
              getNifiEdgeData();
            }
          },
          (error) => {
            errorAlert('Failed to delete the node. Please try again.');
          }
        );
      },
      onCancel: () => {
        // console.log('Deletion canceled');
      },
    });
  }, [nifiData])

  const childNode = (nodeName: string) => {
    DataSourceHandler.scaledNode(
      `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
      nodeName,
      (response: any) => {
        setIsLoading(false);
        if (response) {
          setScaledNodesData(response);
        } else {
          console.error("Unexpected response format or error", response);
          errorAlert("Failed to fetch patch data. Please try again.");
        }
      }
    );
  }

  const scaledNodes = (nodeName: string) => {
    setIsLoading(true);
    setParentDataView(false);
    childNode(nodeName);
  };


  const onEditNode = useCallback((data: any) => {
    setNifiEdgeDataModal(data)
    setNifiEdgeModal(true);
  }, []);

  const filteredData = useMemo(() => {
    if (selectedNodeType === "edgeNodes") {
      return nifiData.filter(node => node.scaleNodes === null);
    } else if (selectedNodeType === "poolNodes") {
      return nifiData.filter(node => typeof node.scaleNodes === "number" && node.scaleNodes >= 0);
    }
    return nifiData;
  }, [nifiData, selectedNodeType]);

  const goToHierarchyPage = (data: NifiEdgeData) => {
    history.push(`/admin/asset-hierarchy/edge-server?nodeName=${data.nodeName}`);
  }

  const CheckForUpdates = () => {
    DataSourceHandler.GetRefreshUpdates(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, (res) => {
      successAlert(res);
    }, (e: string) => {
      errorAlert(e);
    });
  }

  const openMetricLogs = (data: NifiEdgeData, type: string, appType: string) => {
    let appName = "";
    if (data.nodeKind == 'OSIPI') {
      appName = `pi${appType}-logs`;
    } else if (data.nodeKind == 'OPCHDA') {
      appName = `hda${appType}-logs`;
    } else if (data.nodeKind == 'AspenIP21') {
      appName = `ip21${appType}-logs`;
    } else if (data.nodeKind == 'OPCUA') {
      appName = `opcua${appType}-logs`;
    } else if (data.nodeKind == 'Relational') {
      appName = `relational${appType}-logs`;
    } else {
      appName = `ftp${appType}-logs`;
    }
    DataSourceHandler.openMetricLogs(
      `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
      data.nodeName,
      type,
      appName,
      (response: any) => {
        if (response?.fullUrl) {
          const newWindow = window.open(response.fullUrl, "_blank");
          if (newWindow) {
            const timer = setInterval(() => {
              if (newWindow.closed) {
                clearInterval(timer);
                DataSourceHandler.DeleteMetricLogs(
                  `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
                  data.nodeName,
                  type,
                  (res) => {
                    successAlert(res?.message);
                  },
                  (e: any) => {
                    console.error('Error deleting DAG:', e);
                  }
                );
              }
            }, 1000);
          }
        }
      }, (e: any) => {
        console.error('Error opening metric logs:', e);
      }
    );
  }

  return (
    <>
      <ul className={classnames(styles['nifi_titles'])}>
        {isLoading && (
          <div className={styles['spinner-container']}>
            <Spinner />
          </div>
        )}
        {(parentDataView ? filteredData : scaledNodesData).map((_data) => {
          const relatedNode = _data.scaledNodes?.[0]?.parentNode
            ? poolData.find(node => node.nodeName === _data.scaledNodes?.[0]?.parentNode)
            : null;
          const totalScaledNodes = relatedNode?.scaleNodes || 0;
          const onlineScaledNodes = relatedNode?.scaledNodes
            ? relatedNode.scaledNodes.filter((node: any) => node.online).length
            : 0;
          const displayFraction = totalScaledNodes > 0 ? `${onlineScaledNodes}/${totalScaledNodes}` : null;

          return (<li key={_data.nodeName || _data.nodeScaleName}>
            <div className={styles['mt-0']}>
              <TooltipTop title={_data.nodeName || _data.nodeScaleName}>
                <h2 className={styles['title'] + ' ' + styles['truncate']}>
                  {_data.nodeName || _data.nodeScaleName}
                </h2>
              </TooltipTop>
              <div className={styles['badge-section']}>
                {parentDataView && <TooltipTop title="Check For Updates">
                  <div style={{ cursor: 'pointer' }} onClick={() => CheckForUpdates()}>
                    <RestartSVGIcon />
                  </div>
                </TooltipTop>}
                <div
                  className={classnames(styles['status-badge'], {
                    [styles['running']]: _data.online,
                    [styles['terminated']]: !_data.online,
                  })}
                >
                  <div className={styles['circle']}></div>
                  <span className={styles['label']}>
                    {_data.online ? 'Online' : 'Offline'}
                    {displayFraction && (
                      <span className={styles['fraction']}> ({displayFraction})</span>
                    )}
                  </span>
                </div>
                <div className={styles['go-to-full-screen-icon']} style={{ cursor: 'pointer' }} onClick={() => onEdgeNodeClick(_data, '48days')}>
                  <GoToFullScreenIcon />
                </div>
              </div>
            </div>
            <div className={styles['divider']}></div>
            <div>
              <div className={styles['key']}>Node Kind</div>
              <div>{_data.nodeKind}</div>
            </div>
            {!parentDataView && <><div className={styles['divider']}></div>
              <div>
                <div className={styles['key']}>Current Version</div>
                <div>{_data.currentPatchVersion}</div>
              </div></>}
            <div className={styles['divider']}></div>

            {parentDataView && <button
              className={styles['editButton']}
              onClick={() => onEditNode(_data)}
            >
              <div className={styles['center']}>
                <img src='/icons/pencil.svg' alt="Edit" />
                Edit Edge Node
              </div>
            </button>}
            {(parentDataView && !_data.scaleNodes || _data && !parentDataView) && (
              <button
                className={styles['editButton']}
                onClick={() => onDownloadCertificate(_data.id, _data)}
              >
                <div className={styles['center']}>
                  <img src='/icons/certificate.svg' alt="Download" />
                  Download Configuration
                </div>
              </button>
            )}

            {parentDataView && _data.scaleNodes > 0 && <button
              className={styles['editButton']}
              onClick={() => {
                setIsLoading(true);
                scaledNodes(_data.nodeName)
              }}
            >
              <div className={styles['center']}>
                <img src='/icons/scaled-nodes.svg' alt="" />
                Scaled Nodes
              </div>
            </button>}
            {parentDataView && (_data.nodeKind == "OSIPI" || _data.nodeKind == "OPCHDA") && (
              <div className={styles['buttonGroup']}>
                <button
                  className={styles['editButton']}
                  onClick={() => showPatches(_data, 'update')}
                >
                  <div className={styles['center']}>
                    Update
                  </div>
                </button>

                <button
                  className={styles['editButton']}
                  onClick={() => showPatches(_data, 'revert')}
                >
                  <div className={styles['center']}>
                    Revert
                  </div>
                </button>
                <TooltipTop title="Installation Logs">
                  <button
                    className={styles['editButton']}
                    onClick={() => showInstallationLogs(_data)}
                  >
                    <div className={styles['center']}>

                      Logs
                    </div>
                  </button>
                </TooltipTop>
              </div>
            )}
          <ShowWhenTrue show={Env?.SHOW_METRICS_LOGS_BUTTON}>
            <div className={styles['buttonGroup']}>
                <button
                  className={styles['editButton']}
                  onClick={() => openMetricLogs(_data, 'metrics', '')}
                >
                  <div className={styles['center']}>

                    Metrics
                  </div>
                </button>

                <button
                  className={styles['editButton']}
                  onClick={() => openMetricLogs(_data, 'logs', 'nifi')}
                >
                  <div className={styles['center']}>

                    Edge Logs
                  </div>
              </button>
            </div>
          </ShowWhenTrue>
            {/* <button
                    className={styles['editButton']}
                    onClick={() => openMetricLogs(_data, 'logs', 'app')}
                  >
                    <div className={styles['center']}>
                      Python Logs
                    </div>
                  </button> */}

            {!parentDataView && (
              <button
                className={styles['editButton']}
                onClick={() => deleteScaleNode(_data.parentNode, _data.nodeScaleName)}
              >
                <div className={styles['center']}>
                  Delete Node
                </div>
              </button>
            )}
          </li>);
        })}

      </ul>

      {!parentDataView && (
        <button
          className={styles['backButton']}
          onClick={() => {
            setParentDataView(true);
          }}
        >
          <div className={styles['center']}>
            <img src='/icons/back.svg' alt="" />
            Back to Nodes
          </div>
        </button>
      )}
    </>
  );
};

export default NifiTiles;
